import React, { useState } from 'react';
import { MainLayout, Footer } from '@/components/layout';
import MetaTags from 'react-meta-tags';
import { Card, Button } from '@/components/uikit';
import {
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import { useTranslate } from '@/locale';
import { KeyOfDictionary, ErrorRequestHasBeenMade } from '@/types';
import { ReactComponent as DeleteUserSvg } from '@/assets/images/delete_account.svg';
import classnames from 'classnames';
import { OtpApi, sessionApi, userApi } from '@/api';
import {
  regexNotNumber,
  formatPhoneNumber,
  errorHandlingGlobal,
} from '@/utils/helper';
import { useParams, useHistory } from 'react-router';
import { useSnackBar } from '@/store/snackbar/snackbarContext';
import LanguagePreferenceBar from '../LanguagePreferenceBar';
import OtpDialog from './components/OtpDialog';
import NotFoundDialog from './components/NotFoundDialog';

const deleteUserReasons: Record<string, KeyOfDictionary[]> = {
  patient: [
    'applicationNotWorking',
    'cannotFindMedicine',
    'iDontNeed',
    'others',
  ],
  doctor: [
    'applicationNotWorking',
    'manyMedicationNotAvailable',
    'iDontNeed',
    'others',
  ],
};

const userTypeList = ['patient', 'doctor'];

const DeleteUser = () => {
  const { translate, translateToId, currentLanguage } = useTranslate();
  const { userType } = useParams<{ userType: string }>();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [deleteReason, setDeleteReason] = useState<KeyOfDictionary | null>(
    null
  );
  const [notes, setNotes] = useState<string>('');
  const [isOpenOtpDialog, setOpenOtpDialog] = useState<boolean>(false);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [cooldown, setCooldown] = useState(false);
  const { showSnackBar } = useSnackBar();
  const history = useHistory();

  if (userType && !userTypeList.includes(userType)) {
    history.replace('/patient');
  }

  const isDoctor = userType === 'doctor';

  const handleChangePhoneNumber = (value: string) => {
    if (value && regexNotNumber(value)) return;
    setPhoneNumber(value);
  };

  const handleRequestOtp = async () => {
    try {
      if (!isOpenOtpDialog) {
        setLoading(true);
      }
      const requestOtp = isDoctor ? OtpApi.requestDoctorOtp : OtpApi.requestOtp;

      await requestOtp({
        phone_number: `+62${formatPhoneNumber(phoneNumber)}`,
        authentication_mode: 'login',
      });

      // NOTE: handle resent otp
      if (isOpenOtpDialog) {
        showSnackBar(translate('otpSent'), 'info');
        return;
      }

      setOpenOtpDialog(true);
    } catch (error) {
      errorHandlingGlobal(error as ErrorRequestHasBeenMade, {
        '400': errorFromCallback => {
          if (
            errorFromCallback.data.error_code === 'USER_NOT_FOUND' ||
            errorFromCallback.data.error_code === 'OTP_PHONE_NUMBER_NOT_FOUND'
          ) {
            setIsNotFound(true);
          }
        },
        '429': errorFromCallback => {
          if (errorFromCallback.data.error_code === 'OTP_REQUEST_LIMIT') {
            if (!isOpenOtpDialog) {
              setOpenOtpDialog(true);
            }
            setCooldown(true);
          }
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      const getSession = isDoctor
        ? sessionApi.getDoctorSession
        : sessionApi.getPatientSession;
      const res = await getSession({
        phone_number: `+62${formatPhoneNumber(phoneNumber)}`,
        authentication_mode: 'login',
      });
      const token = res?.data?.token;
      if (token) {
        const deleteUser = isDoctor
          ? userApi.deleteUserDoctor
          : userApi.deleteUser;
        await deleteUser(
          {
            reason: translateToId(deleteReason || 'others'),
            notes: deleteReason === 'others' ? notes || '' : '',
          },
          token
        );
        history.replace(
          `/patient/delete-user-result/success?lng=${currentLanguage}`
        );
      } else {
        history.replace(
          `/patient/delete-user-result/failed?lng=${currentLanguage}`
        );
      }
      setOpenOtpDialog(false);
    } catch (error) {
      history.replace(
        `/patient/delete-user-result/failed?lng=${currentLanguage}`
      );
    }
  };

  const handleValidateOtp = async (otpCode: string) => {
    try {
      const validateOtp = isDoctor
        ? OtpApi.validateDoctorOtp
        : OtpApi.validateOtp;
      await validateOtp({
        phone_number: `+62${formatPhoneNumber(phoneNumber)}`,
        code: otpCode,
      });
      handleDeleteUser();
    } catch (error) {
      errorHandlingGlobal(error as ErrorRequestHasBeenMade, {
        '400': errorFromCallback => {
          if (errorFromCallback.data.error_code === 'OTP_NOT_MATCH') {
            showSnackBar(translate('wrongOtp'), 'error');
          }
        },
      });
    }
  };

  const isContinueDisabled = !deleteReason || phoneNumber.length < 8;

  const handleClickContinue = async () => {
    handleRequestOtp();
  };

  return (
    <MainLayout
      hideBanner
      style={{
        marginBottom: '50px',
        backgroundColor: 'white',
      }}
    >
      <MetaTags>
        <title>Delete User</title>
      </MetaTags>
      <LanguagePreferenceBar />
      <Card>
        <div
          style={{ textAlign: 'center', marginBottom: '24px', height: '100%' }}
        >
          <DeleteUserSvg style={{ width: '149px' }} />
          <p style={{ fontWeight: 700, fontSize: '18px' }}>
            {translate('deleteUser')}
          </p>
          <p style={{ fontSize: '14px', color: '#585E5F' }}>
            {translate('deleteUserWillRemoveData')}
          </p>
        </div>
        <TextField
          style={{ width: '100%', marginBottom: '24px' }}
          placeholder={translate('inputPhoneNumber')}
          inputProps={{ inputMode: 'numeric' }}
          value={phoneNumber}
          onChange={e => handleChangePhoneNumber(e.target.value)}
        />
        <p style={{ fontSize: '16px', fontWeight: 600 }}>
          {translate('deleteUserReason')}
        </p>
        <RadioGroup
          aria-label="Delete Reason"
          name="reason"
          value={deleteReason}
          onChange={evt => setDeleteReason(evt.target.value as KeyOfDictionary)}
          style={{ fontSize: '14px' }}
        >
          {deleteUserReasons[userType].map(reason => (
            <FormControlLabel
              key={reason}
              value={reason}
              control={<Radio color="primary" />}
              label={translate(reason)}
            />
          ))}
        </RadioGroup>
        {deleteReason === 'others' && (
          <>
            <p style={{ fontSize: '14px', fontWeight: 400, color: '#585E5F' }}>
              {`${translate('tellUsReason')} `}
              <i>{`(${translate('optional')})`}</i>
            </p>
            <TextField
              multiline
              style={{ width: '100%' }}
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </>
        )}
        <Footer
          style={{ zIndex: 100 }}
          className={classnames({ 'hide-shadow': true })}
        >
          <Button
            borderRadius="20px"
            style={{
              marginLeft: 0,
              padding: '10px',
              borderRadius: '24px',
              width: '100%',
            }}
            onClick={handleClickContinue}
            disabled={isContinueDisabled || isLoading}
            loading={isLoading}
          >
            {translate('deleteUser')}
          </Button>
        </Footer>
      </Card>
      <OtpDialog
        cooldown={cooldown}
        setCooldown={setCooldown}
        isOpen={isOpenOtpDialog}
        onClose={() => setOpenOtpDialog(false)}
        phoneNumber={`0${formatPhoneNumber(phoneNumber)}`}
        handleRequestOtp={handleRequestOtp}
        handleValidateOtp={handleValidateOtp}
      />
      <NotFoundDialog
        isOpen={isNotFound}
        onClose={() => setIsNotFound(false)}
      />
    </MainLayout>
  );
};

export default DeleteUser;
